import { Button, Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../state/store';
import Icon from '../../common/components/Icon/Icon';
import { LrvForm } from '../../common/components/LrvForm/LrvForm';
import { useFormErrorFields } from '../../hooks/useFormErrorFields';
import { LrvModal } from '../../common/components/LrvModal/LrvModal';
import { LrvSwitch } from '../../common/components/LrvSwitch/LrvSwitch';
import { LrvInputV2 } from '../../common/components/LrvInputV2/LrvInputV2';
import { LrvSelectV2 } from '../../common/components/LrvSelectV2/LrvSelectV2';
import { goToOnboardingNextStep } from '../../common/components/Onboarding/OnboardingSlice';
import { isPromoterRole, isSalesRole, isSuperAdmin, LARVIA_ID, roles, maxNumberOfCampusToShow, PATTERN, isBusinessManagerRole, isOwnerRole, isDistributorCompany, isSalesManagerRole } from '../../config/commons';

import styles from './Users.module.scss';
import * as usersSlice from './usersSlice';
import { getValueActiveUsers } from './helpers';
import { User, Campus, EditUser } from './interfaces';

const { Option } = Select;

export const EditUserForm = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const [formEditUser] = Form.useForm();
  const [formAssignOwner] = Form.useForm();
  const [formChangeRole] = Form.useForm();
  
  const { errorFields, extractAndSetErrorFields } = useFormErrorFields({ form: formEditUser });

  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState(['']);
  const [newSeller, setNewSeller] = useState<string | undefined>(undefined);
  const [newUserCampus, setNewUserCampus] = useState(['']);
  const [newPromoters, setNewPromoters] = useState<string[] | undefined>([]);
  const [allowXlsxAnalysisReport, setAllowXlsxAnalysisReport] = useState<boolean>(false);
  const [allowCompanyBalanceConsumption, setAllowCompanyBalanceConsumption] = useState(true);
  const [isMfaEnabled, setIsMfaEnabled] = useState<boolean | undefined>(false);

  const [disabledButtonFormEditUser, setDisabledButtonFormEditUser] = useState(false);
  
  const [updateOwnerRole, setUpdateOwnerRole] = useState('');
  const [assignOwnerUser, setAssignOwnerUser] = useState('');

  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showAssignOwnerModal, setShowAssignOwnerModal] = useState(false);

  const {
    activeUsers,
    showEditUserModal,
    selectedUser,
    sellers,
    promoters,
    campus: companyCampus,
    isUserLoading,
    isFormUpdateLoading,
    hasEmailErrorUpdate,
    hasCurrentPasswordError,
    isUpdatingOwner,
    filters,
    owner,
    isAssigningOwner,
  } = useSelector((state: RootState) => state.users);
  const { company } = useSelector((state: RootState) => state.header);

  const { firstName, lastName, email, campus: userCampus } = selectedUser;
  const { userStatus } = filters;

  const [activeUser, setActiveUser] = useState(true);
  const usersDivRef = useRef<HTMLDivElement>(null);

  const {
    run: isRunningOnboarding,
  } = useSelector((state: RootState) => state.onboarding);

  const showBusinessManagerRole = (((isSuperAdmin() || isSalesRole() || isSalesManagerRole()) && company.isDistributor) || (isDistributorCompany() && isOwnerRole()));
  const hasPermissionToManageCompanies = isSuperAdmin() || isSalesRole() || isSalesManagerRole() || isPromoterRole() || isBusinessManagerRole();

  useEffect(() => {
    if (!selectedUser._id) {
      return;
    }

    const campusId = userCampus?.map((campus) => campus._id) || [''];
    const promoters = selectedUser.promoters?.map((promoter) => promoter._id) || undefined;

    setActiveUser(selectedUser.active);
    setNewUserFirstName(firstName);
    setNewUserLastName(lastName);
    setNewUserEmail(email);
    setNewUserRole(selectedUser.roles);
    setNewUserCampus(campusId);

    setNewPromoters(promoters);
    setAllowCompanyBalanceConsumption(selectedUser.allowCompanyBalanceConsumption);
    setIsMfaEnabled(selectedUser.isMfaEnabled);

    if (selectedUser.allowXlsxAnalysisReport === undefined) {
      setAllowXlsxAnalysisReport(false);
    } else {
      setAllowXlsxAnalysisReport(selectedUser.allowXlsxAnalysisReport);
    }

    formEditUser.setFieldsValue({
      editFirstName: firstName,
      editLastName: lastName,
      editEmail: email,
      editRole: selectedUser.roles,
      editCampuses: campusId,
      editSeller: selectedUser.sellerId,
      editPromoters: promoters,
      allowXlsxAnalysisReport: selectedUser.allowXlsxAnalysisReport,
      allowCompanyBalanceConsumption: selectedUser.allowCompanyBalanceConsumption,
      isMfaEnabled: selectedUser.isMfaEnabled,
    });
  }, [formEditUser, selectedUser._id, selectedUser.active, firstName, lastName, email, selectedUser.roles, userCampus, selectedUser.sellerId, selectedUser.promoters, selectedUser.allowXlsxAnalysisReport, selectedUser.allowCompanyBalanceConsumption]);

  useEffect(() => {
    if (!isUpdatingOwner) {
      setShowChangeRoleModal(false);
      dispatch(usersSlice.resetOwner());
      formChangeRole.resetFields();
    }
  }, [dispatch, formChangeRole, isUpdatingOwner]);

  useEffect(() => {
    if (!isAssigningOwner) {
      setShowAssignOwnerModal(false);
      formAssignOwner.resetFields();
    }
  }, [formAssignOwner, isAssigningOwner]);

  function renderSelectRole () {
    return <LrvSelectV2
      theme='light'
      mode='multiple'
      value={newUserRole}
      label={t('users.role')}
      onChange={onChangeRole}
      removeIcon={<Icon name='close' />}
    >
      <Option value={roles.PARAMETERIST}>
        <div id='edit_role_parameterist'>{t('users.parameterist')}</div>
      </Option>
      <Option value={roles.CLIENT_OPERATOR}>
        <div id='edit_role_operator'>{t('users.clientOperator')}</div>
      </Option>
      <Option value={roles.CLIENT_MANAGER}>
        <div id='edit_role_manager'>{t('users.clientManager')}</div>
      </Option>

      {/* <Option value={roles.TRADING}>
        <div id='edit_role_trading'>{t('users.trading')}</div>
      </Option> */}

      {
        (hasPermissionToManageCompanies) &&
        <Option value={roles.CLIENT_OWNER} >
          <div id='edit_role_owner'> {t('users.clientOwner')} </div>
        </Option>
      }

      {
        showBusinessManagerRole &&
        <Option value={roles.BUSINESS_MANAGER} >
          <div id='edit_role_business_manager'> {t('users.businessManager')} </div>
        </Option>
      }

      {
        (isSuperAdmin() || isSalesRole() || isSalesManagerRole()) && company._id === LARVIA_ID &&
        <Option value={roles.PROMOTER} >
          <div id='edit_role_promoter'> {t('users.clientPromoter')} </div>
        </Option>
      }

      {!(isSuperAdmin() || isSalesRole() || isSalesManagerRole() || isSalesManagerRole() || isPromoterRole() || isBusinessManagerRole()) && newUserRole.includes(roles.CLIENT_OWNER) ?
        <Option value={roles.CLIENT_OWNER}>
          <div id='edit_role_owner'> {t('users.clientOwner')} </div>
        </Option> : null}
    </LrvSelectV2>;
  }

  function validateEditUser () {
    if (!selectedUser.roles.includes(roles.CLIENT_OWNER) && newUserRole.includes(roles.CLIENT_OWNER)) {
      dispatch(usersSlice.fetchClient(selectedUser.companyId));
      return setShowChangeRoleModal(true);
    }

    if (selectedUser.roles.includes(roles.CLIENT_OWNER) && !newUserRole.includes(roles.CLIENT_OWNER)) {
      dispatch(usersSlice.fetchActiveUsers(selectedUser.companyId));
      return setShowAssignOwnerModal(true);
    }

    editUserSelected();
  }

  function getUpdateSellersData () {
    const updateSellersData = [];

    if (!selectedUser.roles.includes(roles.SALES)) {
      return [];
    }

    const selectedPromoters = selectedUser.promoters?.map((promoter) => promoter._id) || [];
    const unassignedPromoters = promoters.map((promoter) => promoter._id) || [];

    const newPromotersIds = newPromoters?.filter(promoter => unassignedPromoters.includes(promoter)) || [];
    const oldPromotersIds = selectedPromoters?.filter(promoter => !newPromoters?.includes(promoter)) || [];

    for (let index = 0; index < newPromotersIds.length; index++) {
      const newPromoterId = newPromotersIds[index];

      const data = {
        userId: newPromoterId,
        companyId: selectedUser.companyId,
        sellerId: selectedUser._id,
      };
      updateSellersData.push(data);
    }

    for (let index = 0; index < oldPromotersIds.length; index++) {
      const oldPromoterId = oldPromotersIds[index];

      const data = {
        userId: oldPromoterId,
        companyId: selectedUser.companyId,
        sellerId: null,
      };
      updateSellersData.push(data);
    }

    return updateSellersData;
  }

  function editUserSelected () {
    const userData: EditUser = {
      userId: selectedUser._id,
      firstName: newUserFirstName,
      lastName: newUserLastName,
      email: newUserEmail,
      roles: newUserRole,
      company: company,
      active: activeUser,
      campus: newUserCampus,
      allowXlsxAnalysisReport,
      allowCompanyBalanceConsumption,
      isMfaEnabled,
    };

    if (newUserRole.includes(roles.PROMOTER)) {
      userData.sellerId = newSeller;
    } else if (selectedUser.roles.includes(roles.PROMOTER)) {
      userData.sellerId = null;
    }

    const activeUsers = getValueActiveUsers(userStatus);
    const updateSellersData = getUpdateSellersData();
    dispatch(usersSlice.updateUser(userData, activeUsers, updateSellersData, resetEditForm));
  }

  const onChangeRole = (value: string[]) => {
    if (newSeller && !value.includes(roles.PROMOTER)) {
      setNewSeller(undefined);
      formEditUser.setFieldsValue({
        seller: undefined,
        editSeller: undefined,
      });
    }
    setNewUserRole(value);
  };


  const resetEditForm = () => {
    dispatch(usersSlice.setShowEditUserModal(false));
    formEditUser.resetFields();
  };

  const onCloseEditModal = () => {
    dispatch(usersSlice.setShowEditUserModal(false));
    formEditUser.resetFields();

    if (isRunningOnboarding) {
      dispatch(goToOnboardingNextStep(800));
    }
  };

  const renderFirstName = () => {
    const hasError = errorFields.includes('editFirstName');

    return (
      <Form.Item
        name='editFirstName'
        rules={[{ required: true, message: t('users.firstNameRequired') }]}
      >
        <LrvInputV2
          theme='light'
          label={t('users.firstName')}
          name='firstName'
          value={newUserFirstName}
          hasError={hasError}
          onChange={(e) => setNewUserFirstName(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderLastName = () => {
    const hasError = errorFields.includes('editLastName');

    return (
      <Form.Item
        name='editLastName'
        rules={[{ required: true, message: t('users.lastNameRequired') }]}
      >
        <LrvInputV2
          theme='light'
          label={t('users.lastName')}
          value={newUserLastName}
          hasError={hasError}
          onChange={(e) => setNewUserLastName(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderEmail = () => {
    const hasError = errorFields.includes('editEmail');

    return (
      <Form.Item
        required
        validateStatus={hasEmailErrorUpdate ? 'error' : undefined}
        help={hasEmailErrorUpdate ? t('users.emailRepeat') : undefined}
        name='editEmail'
        rules={[{ required: true, pattern: PATTERN.EMAIL, message: t('users.emailRequired') }]}
      >
        <LrvInputV2
          theme='light'
          label={t('users.email')}
          value={newUserEmail}
          hasError={hasError}
          onChange={(e) => setNewUserEmail(e.target.value)}
        />
      </Form.Item>
    );
  };

  return (
    <>
      <LrvModal
        theme='light'
        isLoading={isUserLoading || isFormUpdateLoading}
        wrapClassName={styles.editUserModal}
        title={t('users.editUser')}
        open={showEditUserModal}
        closeIcon={<Icon id='close_edit_user_modal' name='close' />}
        destroyOnClose={true}
        footer={[
          <div key='right'>
            <Button
              key='cancel'
              id='cancel_new_user'
              onClick={onCloseEditModal}
            >
              {t('users.cancel')}
            </Button>
            <Button
              id='submit_edit_user'
              style={{ float: 'right' }}
              disabled={disabledButtonFormEditUser}
              type='primary'
              htmlType='submit'
              form='formEditUser'
              className={styles.button}
              loading={isFormUpdateLoading}
            >
              {t('users.save')}
            </Button>
          </div>,
        ]}
        onCancel={onCloseEditModal}
      >
        <div className={styles.contentHeader}>
          <div className={styles.title}>{email ? `${firstName} ${lastName}` : t('users.user')}</div>
          <div className={styles.switch}>
            <div> {t('users.userActive')} </div>
            <LrvSwitch
              theme='light'
              id='active_user_switch'
              className='switchActiveUser'
              checked={activeUser}
              onChange={(value) => setActiveUser(value)}
            />
          </div>
        </div>

        <div className={styles.userData} style={{ height: usersDivRef.current?.offsetHeight }}>
          <LrvForm
            theme='light'
            form={formEditUser}
            name='formEditUser'
            id='formEditUser'
            layout='vertical'
            onFieldsChange={() => {
              setDisabledButtonFormEditUser(
                formEditUser.getFieldsError().filter(({ errors }) => errors.length).length > 0
              );

              extractAndSetErrorFields();
            }}
            onFinish={validateEditUser}
          >
            {renderFirstName()}
            {renderLastName()}
            {renderEmail()}

            <Form.Item
              name='editRole'
              rules={[{ required: true, message: t('users.roleRequired') }]}
            >
              {renderSelectRole()}
            </Form.Item>

            {
              newUserRole.includes(roles.PROMOTER) &&
            <Form.Item
              name='editSeller'
              rules={[{ required: true, message: t('users.sellerRequired') }]}
            >
              <LrvSelectV2
                theme='light'
                value={newSeller}
                onChange={e => setNewSeller(e)}
                label={t('users.seller')}
              >
                {sellers.map((seller) =>
                  <Option key={seller._id} value={seller._id}>
                    {seller.firstName} {seller.lastName}
                  </Option>)}
              </LrvSelectV2>
            </Form.Item>
            }

            <Form.Item
              name='editCampuses'
              rules={[{ required: true, message: t('users.campusRequired') }]}
            >
              <LrvSelectV2
                theme='light'
                mode='multiple'
                label={t('users.campuses')}
                value={newUserCampus}
                maxTagCount={maxNumberOfCampusToShow}
                onChange={setNewUserCampus}
                removeIcon={<Icon name='close' />}
              >
                {companyCampus && companyCampus.map((item: Campus, index) =>
                  <Option key={index} value={item._id}>{item.name}</Option>
                )}
              </LrvSelectV2>
            </Form.Item>

            {
              (newUserRole.includes(roles.SALES) || newUserRole.includes(roles.SALES_MANAGER)) &&
            <Form.Item
              name='editPromoters'
            >
              <LrvSelectV2
                theme='light'
                mode='multiple'
                value={newPromoters}
                onChange={e => setNewPromoters(e)}
                label={t('users.promoters')}
                removeIcon={<Icon name='close' />}
              >
                {selectedUser.promoters?.map((user) =>
                  <Option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </Option>
                )}

                {promoters.map((user) =>
                  <Option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </Option>
                )}
              </LrvSelectV2>
            </Form.Item>
            }

            {
              (company.allowXlsxAnalysisReport && hasPermissionToManageCompanies) &&
              <Form.Item
                name='allowXlsxAnalysisReport'
                label={t('users.allowXlsxAnalysisReport')}
              >
                <LrvSwitch
                  theme='light'
                  id='allow_xlsx_switch'
                  checked={allowXlsxAnalysisReport}
                  onChange={(value) => setAllowXlsxAnalysisReport(value)}
                />
              </Form.Item>
            }

            <Form.Item
              name='allowCompanyBalanceConsumption'
              label={t('users.allowCompanyBalanceConsumption')}
            >
              <LrvSwitch
                theme='light'
                id='allow_company_balance_consumption'
                checked={allowCompanyBalanceConsumption}
                onChange={(value) => setAllowCompanyBalanceConsumption(value)}
              />
            </Form.Item>

            <Form.Item
              name='isMfaEnabled'
              label={t('users.isMfaEnabled')}
            >
              <LrvSwitch
                theme='light'
                id='is_mfa_enabled'
                checked={isMfaEnabled}
                onChange={(value) => setIsMfaEnabled(value)}
              />
            </Form.Item>
          </LrvForm>
        </div>
      </LrvModal>

      <LrvModal
        theme='light'
        className='updateOwnerModal'
        title={t('users.ownerUpdated.title')}
        open={showChangeRoleModal}
        destroyOnClose={true}
        okButtonProps={{ id: 'submit_update_owner', disabled: !updateOwnerRole, htmlType: 'submit', form: 'formChangeRole', loading: isUpdatingOwner }}
        cancelButtonProps={{ id: 'cancel_update_owner' }}
        closeIcon={<Icon id='close_update_owner_modal' name='close' />}
        onOk={() => {
          const currentOwnerData = { userId: owner.owner._id, roles: [updateOwnerRole], companyId: selectedUser.companyId };

          dispatch(usersSlice.updateOwner(currentOwnerData));
          editUserSelected();
          setUpdateOwnerRole('');
        }}
        okText={t('users.save')}
        cancelText={t('users.cancel')}
        onCancel={() => {
          setUpdateOwnerRole('');
          dispatch(usersSlice.resetOwner());
          setShowChangeRoleModal(false);
          formChangeRole.resetFields(['change_role']);
        }}
      >
        <LrvForm
          theme='light'
          form={formChangeRole}
          name='formChangeRole'
          id='formChangeRole'
          layout='vertical'
        >
          <Form.Item
            name='text'
          >
            <div className={styles.changeOwner}>
              {t('users.ownerUpdated.currentOwnerDescription')}
              <br />
              <br />
              <ul>
                <li>{t('users.ownerUpdated.newOwner')}: {selectedUser?.firstName} {selectedUser?.lastName}</li>
                <li>{t('users.ownerUpdated.currentOwner')}: {owner.owner?.firstName} {owner.owner?.lastName}</li>
              </ul>
            </div>
          </Form.Item>

          <Form.Item
            name='change_role'
            validateStatus={hasCurrentPasswordError ? 'error' : undefined}
            rules={[{ required: true, message: t('users.roleRequired') }]}
          >
            <LrvSelectV2
              label={t('users.role')}
              theme='light'
              value={updateOwnerRole}
              onChange={setUpdateOwnerRole}
              style={{ 'width': '100%' }}
            >
              <Option value={roles.CLIENT_OPERATOR}>
                <div id='change_role_operator'>{t('users.clientOperator')}</div>
              </Option>
              <Option value={roles.CLIENT_MANAGER}>
                <div id='change_role_manager'>{t('users.clientManager')}</div>
              </Option>
            </LrvSelectV2>
          </Form.Item>
        </LrvForm>
      </LrvModal>

      <LrvModal
        theme='light'
        className='assignOwnerModal'
        title={t('users.assignOwner.title')}
        open={showAssignOwnerModal}
        destroyOnClose={true}
        okButtonProps={{ id: 'submit_select_owner', disabled: !assignOwnerUser, htmlType: 'submit', form: 'formAssignOwner', loading: isAssigningOwner }}
        cancelButtonProps={{ id: 'cancel_update_owner' }}
        closeIcon={<Icon id='close_update_owner_modal' name='close' />}
        onOk={() => {
          const currentOwnerData = { userId: assignOwnerUser, companyId: selectedUser.companyId };

          dispatch(usersSlice.assignOwner(currentOwnerData));
          editUserSelected();
          setAssignOwnerUser('');
        }}
        okText={t('users.save')}
        cancelText={t('users.cancel')}
        onCancel={() => {
          setAssignOwnerUser('');
          setShowAssignOwnerModal(false);
          formAssignOwner.resetFields(['assign_owner']);
        }}
      >
        <LrvForm
          theme='light'
          form={formAssignOwner}
          name='formAssignOwner'
          id='formAssignOwner'
          layout='vertical'
        >
          <Form.Item
            name='text'
          >
            <div className={styles.changeOwner}>
              {t('users.assignOwner.description')}
            </div>
          </Form.Item>

          <Form.Item
            name='assign_owner'
            rules={[{ required: true, message: t('users.userRequired') }]}
          >
            <LrvSelectV2
              label={t('users.user')}
              theme='light'
              value={assignOwnerUser}
              showSearch
              onChange={(e: string) => {
                setAssignOwnerUser(e);
              }}
            >
              {
                activeUsers.map((user: User, index) => {
                  return <Option key={index} value={user._id}>{user.firstName + ' ' + user.lastName}</Option>;
                })
              }
            </LrvSelectV2>
          </Form.Item>
        </LrvForm>
      </LrvModal>
    </>
  );
};
